import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Bell, BellRing, Check, Smartphone, TrendingUp, TrendingDown } from "lucide-react";
import { enablePush, needsInstallFirst, pushPermission, pushSupported } from "@/lib/push";

const timeAgo = (iso) => {
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
};

/** Pick alerts: bell + unread badge, live toasts, and the phone-push opt-in. */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(pushPermission());
  const [busy, setBusy] = useState(false);
  const seen = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.items);
      setUnread(data.unread);
      const newest = data.items[0];
      // Toast only for alerts that arrived after this tab loaded
      if (newest && seen.current && newest.notif_id !== seen.current && !newest.read_at) {
        toast.success(newest.title, { description: newest.body });
      }
      if (newest) seen.current = newest.notif_id;
    } catch (e) { /* bell stays quiet */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read", {}).catch(() => {});
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
  };

  const turnOnPush = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      setPermission(pushPermission());
      if (res.ok) {
        toast.success("Phone alerts on — we'll ping you when your picks move");
        await api.post("/push/test").catch(() => {});
      } else if (res.reason === "denied") {
        toast.error("Notifications are blocked in your browser settings for this site");
      } else if (res.reason === "not_configured") {
        toast.error("Push isn't configured on this environment yet");
      }
    } catch (e) {
      toast.error("Could not turn on alerts on this device");
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markAll();
  };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        data-testid="notification-bell-btn"
        title="Pick alerts"
        className="relative p-2 rounded-sm hover:bg-white/10 text-white/60 hover:text-white"
      >
        {unread > 0 ? <BellRing size={17} className="text-[#00FF66]" /> : <Bell size={17} />}
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[95]" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-[min(92vw,360px)] max-h-[70vh] overflow-y-auto z-[96] border border-white/10 bg-[#0f0f0f] shadow-2xl"
            data-testid="notification-panel"
          >
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <div className="font-display text-lg">PICK ALERTS</div>
              <button
                onClick={markAll}
                data-testid="notifications-mark-read-btn"
                className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1"
              >
                <Check size={11} /> Mark read
              </button>
            </div>

            {pushSupported() && permission !== "granted" && (
              <div className="p-3 border-b border-white/10 bg-[#00FF66]/[0.05]">
                <div className="text-white/70 text-xs mb-2">
                  Get a buzz on your phone the moment a pick jumps into — or falls out of — the top five.
                </div>
                {needsInstallFirst() ? (
                  <div className="text-[#FF9F0A] text-[10px] uppercase tracking-widest"
                    data-testid="push-install-first-note">
                    On iPhone: Share → Add to Home Screen, then open the app and turn alerts on
                  </div>
                ) : (
                  <button
                    onClick={turnOnPush}
                    disabled={busy || permission === "denied"}
                    data-testid="enable-push-btn"
                    className="w-full py-2 bg-[#00FF66] hover:bg-[#00e65b] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-[11px] rounded-sm inline-flex items-center justify-center gap-1.5"
                  >
                    <Smartphone size={13} />
                    {permission === "denied" ? "Blocked in browser settings" : "Enable phone alerts"}
                  </button>
                )}
              </div>
            )}

            {items.length === 0 ? (
              <div className="p-6 text-center text-white/40 text-sm" data-testid="notifications-empty">
                No alerts yet. Enter a contest and we&apos;ll tell you the moment your picks move.
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {items.map((n) => (
                  <Link
                    key={n.notif_id}
                    to={n.url || "/entries"}
                    onClick={() => setOpen(false)}
                    data-testid={`notification-item-${n.notif_id}`}
                    className={`block p-3 hover:bg-white/5 ${n.read_at ? "" : "bg-[#00FF66]/[0.04]"}`}
                  >
                    <div className="flex items-start gap-2">
                      {n.dropped?.length && !n.entered?.length ? (
                        <TrendingDown size={14} className="text-[#FF3B30] mt-0.5 flex-shrink-0" />
                      ) : (
                        <TrendingUp size={14} className="text-[#00FF66] mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{n.title}</div>
                        <div className="text-white/60 text-xs">{n.body}</div>
                        <div className="text-white/30 text-[10px] uppercase tracking-widest mt-0.5">
                          {timeAgo(n.created_at)} ago
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
